// Follow/unfollow toggle. Works for both organizers (vendor-only
// followers, legacy behavior) AND vendors (anyone can follow). Fetches
// the current state on mount so the label is always accurate, then
// optimistically flips on click. Emits a lightweight window event
// ("vendtour:follows-changed") so my-follows pages can refresh without
// a full remount.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

export default function FollowButton({
  organizerId,
  organizerName,
  vendorId,
  vendorName,
  size = "sm",
  className = "",
  testId,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Pick the active target. Caller passes EITHER organizerId or vendorId.
  const targetKind = vendorId ? "vendor" : "organizer";
  const targetId = vendorId || organizerId;
  const targetName = vendorName || organizerName;
  const endpointBase = targetKind === "vendor"
    ? "/follow/vendor" : "/follow/organizer";

  const [following, setFollowing] = useState(false);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!targetId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`${endpointBase}/${targetId}`);
        if (cancelled) return;
        setFollowing(!!data.following);
        setCount(data.follower_count || 0);
      } catch { /* silent */ }
      finally { if (!cancelled) setReady(true); }
    })();
    return () => { cancelled = true; };
  }, [targetId, endpointBase, user?.id]);

  if (!targetId) return null;
  // Owner of the profile can't follow themselves.
  if (user && user.id === targetId) return null;
  // For organizer follows we keep the legacy "vendors only can follow"
  // rule. For vendor follows anyone (any role, even organizers) can
  // follow because an organizer might want to know when a vendor is
  // appearing at other events.
  if (targetKind === "organizer" && user && user.role !== "vendor") return null;

  const toggle = async () => {
    if (!user) {
      navigate("/login?next=" + encodeURIComponent(window.location.pathname));
      return;
    }
    if (busy || !ready) return;
    setBusy(true);
    const next = !following;
    setFollowing(next);
    setCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      if (next) {
        await api.post(`${endpointBase}/${targetId}`);
        toast.success(
          targetName
            ? targetKind === "vendor"
              ? `Following ${targetName} — you'll hear when they're at a new event.`
              : `Following ${targetName} — you'll get their new events.`
            : "You'll get updates."
        );
      } else {
        await api.delete(`${endpointBase}/${targetId}`);
        toast.success("Unfollowed");
      }
      window.dispatchEvent(new Event("vendtour:follows-changed"));
    } catch (err) {
      setFollowing(!next);
      setCount((c) => Math.max(0, c + (next ? -1 : 1)));
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message
      );
    } finally {
      setBusy(false);
    }
  };

  const label = following ? "Following" : "Follow";

  return (
    <Button
      type="button"
      variant={following ? "outline" : "default"}
      size={size}
      onClick={toggle}
      disabled={busy}
      data-testid={testId || `follow-${targetKind}-${targetId}`}
      className={`rounded-full inline-flex items-center gap-1.5 ${className} ${following ? "" : "btn-primary"}`}
    >
      <Star size={13} fill={following ? "currentColor" : "none"} />
      {label}
      {count > 0 && (
        <span className="text-[10px] opacity-70 ml-1">· {count}</span>
      )}
    </Button>
  );
}
